import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { ThemeProvider, CssBaseline, GlobalStyles } from '@mui/material';

import { darkTheme, lightTheme } from '../theme/mui-theme';

export type ColorModePreference = 'light' | 'dark' | 'system';
export type ResolvedColorMode = 'light' | 'dark';

const COLOR_MODE_STORAGE_KEY = 'paddiq:color-mode';
const INTERFACE_PREFERENCES_STORAGE_KEY = 'paddiq:interface-preferences';

export type InterfaceTextSize = 'default' | 'large' | 'extra-large';
export type InterfaceDensity = 'compact' | 'comfortable';
export type SidebarDefault = 'remember' | 'open' | 'collapsed';

export interface InterfacePreferences {
  textSize: InterfaceTextSize;
  density: InterfaceDensity;
  sidebarDefault: SidebarDefault;
  reduceMotion: boolean;
  highContrast: boolean;
}

const DEFAULT_INTERFACE_PREFERENCES: InterfacePreferences = {
  textSize: 'default',
  density: 'compact',
  sidebarDefault: 'remember',
  reduceMotion: false,
  highContrast: false,
};

interface InterfacePreferencesContextValue {
  preferences: InterfacePreferences;
  setPreference: <Key extends keyof InterfacePreferences>(key: Key, value: InterfacePreferences[Key]) => void;
  resetPreferences: () => void;
}

const InterfacePreferencesContext = createContext<InterfacePreferencesContextValue>({
  preferences: DEFAULT_INTERFACE_PREFERENCES,
  setPreference: () => undefined,
  resetPreferences: () => undefined,
});

export function useInterfacePreferences() {
  return useContext(InterfacePreferencesContext);
}

function loadInterfacePreferences(): InterfacePreferences {
  if (typeof window === 'undefined') {
    return DEFAULT_INTERFACE_PREFERENCES;
  }

  try {
    const stored = JSON.parse(window.localStorage.getItem(INTERFACE_PREFERENCES_STORAGE_KEY) ?? '{}') as Partial<InterfacePreferences>;
    return { ...DEFAULT_INTERFACE_PREFERENCES, ...stored };
  } catch {
    return DEFAULT_INTERFACE_PREFERENCES;
  }
}

interface ColorModeContextValue {
  mode: ColorModePreference;
  resolvedMode: ResolvedColorMode;
  toggle: () => void;
  setMode: (mode: ColorModePreference) => void;
}

const ColorModeContext = createContext<ColorModeContextValue>({
  mode: 'system',
  resolvedMode: 'dark',
  toggle: () => undefined,
  setMode: () => undefined,
});

export function useColorMode() {
  return useContext(ColorModeContext);
}

function isColorModePreference(value: string | null): value is ColorModePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

function loadStoredColorMode(defaultMode: ColorModePreference): ColorModePreference {
  if (typeof window === 'undefined') {
    return defaultMode;
  }

  const stored = window.localStorage.getItem(COLOR_MODE_STORAGE_KEY);
  return isColorModePreference(stored) ? stored : defaultMode;
}

function persistColorMode(mode: ColorModePreference) {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.setItem(COLOR_MODE_STORAGE_KEY, mode);
}

function getSystemColorMode(): ResolvedColorMode {
  if (typeof window === 'undefined') {
    return 'dark';
  }

  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

interface PaddiqProviderProps {
  children: React.ReactNode;
  defaultMode?: ColorModePreference;
}

/**
 * PaddiqProvider — wraps the entire app with the Paddiq MUI theme.
 * Exposes `useColorMode()` hook so any component can toggle light / dark / system.
 * Use at the root of web, desktop, and mobile/RNW apps.
 */
export function PaddiqProvider({ children, defaultMode = 'system' }: PaddiqProviderProps) {
  const [mode, setModeState] = useState<ColorModePreference>(() => loadStoredColorMode(defaultMode));
  const [systemMode, setSystemMode] = useState<ResolvedColorMode>(() => getSystemColorMode());
  const [interfacePreferences, setInterfacePreferences] = useState<InterfacePreferences>(loadInterfacePreferences);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = (event: MediaQueryListEvent) => {
      setSystemMode(event.matches ? 'dark' : 'light');
    };

    setSystemMode(mediaQuery.matches ? 'dark' : 'light');
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  const resolvedMode: ResolvedColorMode = mode === 'system' ? systemMode : mode;
  const theme = resolvedMode === 'dark' ? darkTheme : lightTheme;

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    document.documentElement.style.colorScheme = resolvedMode;
  }, [resolvedMode]);

  const setMode = useCallback((next: ColorModePreference) => {
    setModeState(next);
    persistColorMode(next);
  }, []);

  const ctx = useMemo<ColorModeContextValue>(
    () => ({
      mode,
      resolvedMode,
      setMode,
      toggle: () => setMode(resolvedMode === 'dark' ? 'light' : 'dark'),
    }),
    [mode, resolvedMode, setMode],
  );

  const interfaceContext = useMemo<InterfacePreferencesContextValue>(() => ({
    preferences: interfacePreferences,
    setPreference: (key, value) => {
      setInterfacePreferences((current) => {
        const next = { ...current, [key]: value };
        try {
          if (typeof window !== 'undefined') {
            window.localStorage.setItem(INTERFACE_PREFERENCES_STORAGE_KEY, JSON.stringify(next));
          }
        } catch {
          // Keep the in-memory preference when storage is unavailable.
        }
        return next;
      });
    },
    resetPreferences: () => {
      setInterfacePreferences(DEFAULT_INTERFACE_PREFERENCES);
      try {
        if (typeof window !== 'undefined') {
          window.localStorage.removeItem(INTERFACE_PREFERENCES_STORAGE_KEY);
        }
      } catch {
        // Storage may be blocked in private or embedded browser contexts.
      }
    },
  }), [interfacePreferences]);

  useEffect(() => {
    const root = document.documentElement;
    root.style.fontSize = {
      default: '90%',
      large: '100%',
      'extra-large': '112.5%',
    }[interfacePreferences.textSize];
    root.dataset.paddiqDensity = interfacePreferences.density;
    root.dataset.paddiqContrast = String(interfacePreferences.highContrast);
  }, [interfacePreferences]);

  return (
    <ColorModeContext.Provider value={ctx}>
      <InterfacePreferencesContext.Provider value={interfaceContext}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <GlobalStyles styles={{
            ...(interfacePreferences.reduceMotion ? {
              '*, *::before, *::after': {
                animationDuration: '0.01ms !important',
                animationIterationCount: '1 !important',
                scrollBehavior: 'auto !important',
                transitionDuration: '0.01ms !important',
              },
            } : {}),
            ...(interfacePreferences.density === 'comfortable' ? {
              '.MuiButton-root, .MuiToggleButton-root': { minHeight: 40 },
              '.MuiInputBase-root': { minHeight: 42 },
              '.MuiMenuItem-root': { minHeight: 40 },
            } : {}),
            ...(interfacePreferences.highContrast ? {
              '.MuiPaper-root, .MuiCard-root, .MuiOutlinedInput-notchedOutline, .MuiDivider-root, .MuiToggleButton-root': {
                borderColor: `${resolvedMode === 'dark' ? 'rgba(255,255,255,0.34)' : 'rgba(17,24,39,0.34)'} !important`,
              },
              '.MuiTypography-colorTextSecondary': {
                color: `${resolvedMode === 'dark' ? 'rgba(255,255,255,0.82)' : 'rgba(17,24,39,0.82)'} !important`,
              },
            } : {}),
          }} />
          {children}
        </ThemeProvider>
      </InterfacePreferencesContext.Provider>
    </ColorModeContext.Provider>
  );
}

export const GridlineProvider = PaddiqProvider;
